import { createFileRoute } from "@tanstack/react-router";
import { useQuery, useMutation } from "@tanstack/react-query";
import { useState } from "react";
import { api } from "@/lib/api";
import { useToast } from "@/components/Toaster";
import { meQueryOptions } from "@/lib/queries";

export const Route = createFileRoute("/_auth/settings/about")({
  component: AboutPage,
});

interface HealthResponse {
  status: string;
  version: string;
  org: string;
}

interface LicenseResponse {
  identifier: string;
  name: string;
  notice_body: string;
  license_body: string;
  licensing_contact: string;
}

function Row({ label, value, mono = false }: { label: string; value: React.ReactNode; mono?: boolean }) {
  return (
    <div className="flex items-center justify-between py-3 border-b border-slate-100 dark:border-gray-800 last:border-0">
      <span className="text-sm text-slate-500 dark:text-slate-400">{label}</span>
      <span className={`text-sm font-medium text-slate-900 dark:text-slate-100 ${mono ? "font-mono" : ""}`}>
        {value}
      </span>
    </div>
  );
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div className="bg-white dark:bg-gray-900 rounded-lg border border-slate-200 dark:border-gray-800 overflow-hidden mb-5">
      <div className="px-5 py-3 bg-slate-50 dark:bg-gray-800 border-b border-slate-200 dark:border-gray-700">
        <h2 className="text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wider">{title}</h2>
      </div>
      <div className="px-5">{children}</div>
    </div>
  );
}

function AboutPage() {
  const { data: health } = useQuery({
    queryKey: ["health"],
    queryFn: () => api<HealthResponse>("/health"),
    staleTime: 60_000,
  });

  // Gate detailed build / stack info to admins only. Non-admins see a
  // version-and-status-only view — no commit SHA, no framework versions,
  // no provider names. Reduces information disclosure to anyone who
  // happens to land on this page (curious users, screen-shared meetings,
  // would-be attackers fingerprinting the stack).
  const { data: me } = useQuery(meQueryOptions());
  const isAdmin = me?.role === "admin";

  // License — fetched once; small payload (LICENSE + LICENSE-NOTICE.md)
  const { data: license } = useQuery({
    queryKey: ["license"],
    queryFn: () => api<LicenseResponse>("/api/help/license"),
    staleTime: Infinity,   // license doesn't change between builds
  });

  const [licenseDialog, setLicenseDialog] = useState<null | "notice" | "full">(null);
  const toast = useToast();

  const restart = useMutation({
    mutationFn: () => api<{ status: string }>("/api/config/restart", { method: "POST" }),
    onSuccess: () => toast.success("Server restarting… back in ~5 seconds"),
    onError: (err: Error) => toast.error(`Restart failed: ${err.message}`),
  });

  return (
    <div className="flex flex-col h-[calc(100vh-56px)] overflow-hidden">
      <div className="bg-white border-b border-slate-200 px-6 py-4 dark:bg-gray-900 dark:border-gray-800 flex-shrink-0">
        <h1 className="text-xl font-semibold text-slate-900 dark:text-slate-100">About Ledger Core</h1>
        <p className="text-sm text-slate-500 dark:text-slate-400 mt-0.5">Version information and build details</p>
      </div>

      <div className="overflow-y-auto flex-1 px-6 py-6 bg-slate-50 dark:bg-gray-950">
        <div className="max-w-xl">

          {/* Logo / wordmark */}
          <div className="bg-white dark:bg-gray-900 rounded-lg border border-slate-200 dark:border-gray-800 px-5 py-6 mb-5 flex items-center gap-4">
            <div className="w-12 h-12 rounded-xl bg-blue-600 flex items-center justify-center flex-shrink-0">
              <span className="text-white font-bold text-xl">LC</span>
            </div>
            <div>
              <p className="text-lg font-bold text-slate-900 dark:text-slate-100">Ledger Core</p>
              <p className="text-sm text-slate-500 dark:text-slate-400">Construction accounting &amp; ERP</p>
            </div>
            <div className="ml-auto text-right">
              <p className="text-2xl font-bold text-blue-600">v{__APP_VERSION__}</p>
              <p className="text-xs text-slate-400 dark:text-slate-500 mt-0.5">Current version</p>
            </div>
          </div>

          {/* Status — always shown */}
          <Section title="System">
            <Row label="Version"      value={`v${__APP_VERSION__}`} />
            <Row label="Organisation" value={health?.org ?? "—"} />
            <Row label="Status"       value={
              health ? (
                <span className="inline-flex items-center gap-1.5">
                  <span className="w-2 h-2 rounded-full bg-green-500" />
                  <span className="text-green-600 dark:text-green-400">Online</span>
                </span>
              ) : (
                <span className="inline-flex items-center gap-1.5">
                  <span className="w-2 h-2 rounded-full bg-slate-300" />
                  <span className="text-slate-400">Checking…</span>
                </span>
              )
            } />
          </Section>

          {/* License — always shown */}
          <Section title="License">
            <Row
              label="Identifier"
              value={
                <span className="font-mono">{license?.identifier ?? "—"}</span>
              }
            />
            <Row
              label="Name"
              value={license?.name ?? "—"}
            />
            <Row
              label="Commercial licensing"
              value={
                license ? (
                  <a
                    href={`mailto:${license.licensing_contact}`}
                    className="text-blue-600 hover:underline dark:text-blue-400"
                  >
                    {license.licensing_contact}
                  </a>
                ) : "—"
              }
            />
            <div className="py-3 flex gap-3 items-center justify-end">
              <button
                type="button"
                onClick={() => setLicenseDialog("notice")}
                className="text-sm text-blue-600 hover:underline dark:text-blue-400"
                disabled={!license}
              >
                What this means →
              </button>
              <button
                type="button"
                onClick={() => setLicenseDialog("full")}
                className="text-sm text-blue-600 hover:underline dark:text-blue-400"
                disabled={!license}
              >
                View full license →
              </button>
            </div>
          </Section>

          {/* Admin-only diagnostic details */}
          {isAdmin && (
            <>
              <Section title="Frontend (admin)">
                <Row label="Build commit"  value={__GIT_COMMIT__} mono />
                <Row label="Build date"    value={__BUILD_DATE__} mono />
                <Row label="Environment"   value={import.meta.env.MODE} mono />
              </Section>

              <Section title="Backend (admin)">
                <Row label="API version" value={health ? `v${health.version}` : "—"} />
                <div className="py-3 flex items-center justify-between">
                  <div>
                    <p className="text-sm text-slate-700 dark:text-slate-300 font-medium">Restart server</p>
                    <p className="text-xs text-slate-400 dark:text-slate-500 mt-0.5">Restarts the API container. Takes ~5 seconds.</p>
                  </div>
                  <button
                    type="button"
                    onClick={() => restart.mutate()}
                    disabled={restart.isPending}
                    className="inline-flex items-center gap-1.5 text-sm font-medium px-3 py-1.5 rounded-md bg-amber-50 hover:bg-amber-100 text-amber-700 border border-amber-200 dark:bg-amber-900/20 dark:text-amber-400 dark:border-amber-800 disabled:opacity-50"
                  >
                    {restart.isPending ? "Restarting…" : "↺ Restart"}
                  </button>
                </div>
              </Section>
            </>
          )}

          <p className="text-xs text-center text-slate-400 dark:text-slate-600 mt-2">
            © {new Date().getFullYear()} Bynaus. All rights reserved.
          </p>

        </div>
      </div>

      {/* License text dialog — fixed overlay, dismissed by clicking the backdrop or Esc */}
      {licenseDialog && license && (
        <div
          className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4"
          onClick={() => setLicenseDialog(null)}
          onKeyDown={(e) => e.key === "Escape" && setLicenseDialog(null)}
          role="dialog"
          aria-modal="true"
        >
          <div
            className="bg-white dark:bg-gray-900 rounded-lg max-w-3xl w-full max-h-[80vh] flex flex-col shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="px-5 py-3 border-b border-slate-200 dark:border-gray-800 flex items-center justify-between">
              <h3 className="font-semibold text-slate-900 dark:text-slate-100">
                {licenseDialog === "notice" ? "License — Plain English Summary" : "License — Full Text"}
              </h3>
              <button
                type="button"
                onClick={() => setLicenseDialog(null)}
                className="text-slate-400 hover:text-slate-600 dark:hover:text-slate-200 text-xl leading-none"
                aria-label="Close"
              >
                ×
              </button>
            </div>
            <pre className="px-5 py-4 overflow-y-auto text-xs font-mono whitespace-pre-wrap text-slate-700 dark:text-slate-300 flex-1">
              {licenseDialog === "notice" ? license.notice_body : license.license_body}
            </pre>
            <div className="px-5 py-3 border-t border-slate-200 dark:border-gray-800 flex justify-end gap-2 bg-slate-50 dark:bg-gray-950">
              {licenseDialog === "notice" && (
                <button
                  type="button"
                  onClick={() => setLicenseDialog("full")}
                  className="text-sm px-3 py-1.5 rounded border border-slate-300 hover:bg-slate-100 dark:border-gray-700 dark:hover:bg-gray-800"
                >
                  View full license
                </button>
              )}
              {licenseDialog === "full" && (
                <button
                  type="button"
                  onClick={() => setLicenseDialog("notice")}
                  className="text-sm px-3 py-1.5 rounded border border-slate-300 hover:bg-slate-100 dark:border-gray-700 dark:hover:bg-gray-800"
                >
                  Plain-English summary
                </button>
              )}
              <button
                type="button"
                onClick={() => setLicenseDialog(null)}
                className="text-sm px-3 py-1.5 rounded bg-blue-600 hover:bg-blue-700 text-white"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
